// ============================================================================
//  CrmOpportunityDetail.tsx — Vue détail complète d'une opportunité
//  Affiche : infos, historique étapes, activités, commissions, séquences actives
// ============================================================================

import { useState } from 'react'
import ReactDOM from 'react-dom'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, Activity, Clock, DollarSign, Zap, Check, ChevronRight,
  Edit2, Archive, BookOpen, Play, Square, MoreVertical,
} from 'lucide-react'
import { crmApi } from '@/modules/crm/api/crm.api'
import { useNotificationStore } from '@/stores/notification.store'
import { ActivityCorrectedBadge, ActivityCorrectionList, CorrectActivityButton } from '@/modules/crm/components/ActivityCorrections'

const Spinner = () => <div className="w-5 h-5 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />

const fmtMoney = (n?: any, currency = 'XOF') => {
  const v = typeof n === 'number' ? n : parseFloat(n ?? '0') || 0
  return new Intl.NumberFormat('fr-SN', { style: 'currency', currency, minimumFractionDigits: 0 }).format(v)
}
const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('fr-SN') : '—'
const fmtDateTime = (d?: string) => d ? new Date(d).toLocaleDateString('fr-SN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'

const SCORE_COLORS: Record<string, string> = { HOT: '#DC2626', WARM: '#EA580C', COLD: 'var(--blue-d)' }

const ACTIVITY_ICONS: Record<string, string> = {
  CALL: '📞', EMAIL: '✉️', MEETING: '🤝', VIDEO_CALL: '🎥',
  WHATSAPP: '💬', DEMO: '🖥️', NOTE: '📝', OTHER: '📌',
}

const STATUS_COLORS: Record<string, { color: string; bg: string }> = {
  PENDING:          { color: 'var(--tx2)', bg: '#D6D2EC20' },
  MANAGER_APPROVED: { color: 'var(--blue-d)', bg: '#BAE6FD40' },
  HR_APPROVED:      { color: '#4F46E5', bg: '#C7D2FE40' },
  FINANCE_APPROVED: { color: '#7C3AED', bg: '#DDD6FE40' },
  INTEGRATED:       { color: '#059669', bg: '#D1FAE540' },
  PAID:             { color: '#16A34A', bg: '#BBF7D040' },
  CONTESTED:        { color: '#D97706', bg: '#FDE68A40' },
  REJECTED:         { color: '#DC2626', bg: '#FEE2E240' },
}

export function OpportunityDetailPage() {
  const { id }       = useParams<{ id: string }>()
  const navigate     = useNavigate()
  const queryClient  = useQueryClient()
  const addNotif     = useNotificationStore(s => s.addNotification)
  const [tab, setTab] = useState<'activites' | 'historique' | 'commissions' | 'sequences'>('activites')
  const [showJournal, setShowJournal] = useState(false)
  const [showSeqStart, setShowSeqStart] = useState(false)
  const [approvingStep, setApprovingStep] = useState<{ oppSeqId: string; stepId: string } | null>(null)

  const { data: opp, isLoading } = useQuery({
    queryKey: ['crm-opp-detail', id],
    queryFn:  () => crmApi.getOpportunity(id!),
    enabled:  !!id,
  })

  const archiveMutation = useMutation({
    mutationFn: () => crmApi.deleteOpportunity(id!),
    onSuccess:  () => { addNotif({ id: Date.now().toString(), type: 'success', title: 'Opportunité archivée' }); navigate('/crm/pipeline') },
  })

  const stopSeqMutation = useMutation({
    mutationFn: () => crmApi.stopSequenceOnOpp(id!),
    onSuccess:  () => { queryClient.invalidateQueries({ queryKey: ['crm-opp-detail', id] }); addNotif({ id: Date.now().toString(), type: 'success', title: 'Séquence arrêtée' }) },
  })

  const approveStepMutation = useMutation({
    mutationFn: ({ oppSeqId, stepId }: { oppSeqId: string; stepId: string }) =>
      crmApi.approveSequenceStep({ oppSeqId, stepId }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['crm-opp-detail', id] }); addNotif({ id: Date.now().toString(), type: 'success', title: 'Étape approuvée — email en cours d\'envoi' }) },
    onError:   (err: any) => addNotif({ id: Date.now().toString(), type: 'error', title: err?.response?.data?.message ?? 'Erreur' }),
  })

  if (isLoading || !opp) return (
    <div className="flex justify-center items-center h-64"><Spinner /></div>
  )

  const activities   = (opp as any).activities   ?? []
  const stageHistory = (opp as any).stageHistory  ?? []
  const commissions  = (opp as any).commissions   ?? []
  const seqList      = (opp as any).emailSequences ?? []
  const activeSeq    = seqList.find((s: any) => s.status === 'ACTIVE')

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg text-[var(--tx3)] hover:text-[var(--brand-dark)] hover:bg-[var(--surface-2)] mt-1">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--brand-dark)', letterSpacing: '-0.02em' }}>{opp.title}</h1>
            <span className="text-xs px-2 py-1 rounded-full font-semibold"
              style={{ background: SCORE_COLORS[opp.scoreLevel] + '20', color: SCORE_COLORS[opp.scoreLevel] }}>
              {opp.scoreLevel}
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full"
              style={{ background: 'var(--brand-orange)20', color: 'var(--blue-d)' }}>
              {opp.stage?.name}
            </span>
          </div>
          {opp.company && <p className="text-sm text-[var(--tx3)] mt-1">🏢 {opp.company.name}</p>}
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={() => setShowJournal(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium"
            style={{ background: 'var(--brand-orange)', color: '#FFFFFF' }}>
            <BookOpen className="w-3.5 h-3.5" /> + Journal
          </button>
          <button onClick={() => window.confirm('Archiver cette opportunité ?') && archiveMutation.mutate()}
            className="p-2 rounded-lg text-[var(--tx3)] hover:text-red-500 hover:bg-red-50">
            <Archive className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Montant',      value: fmtMoney(opp.amount, opp.currency), icon: DollarSign, color: '#059669' },
          { label: 'Probabilité',  value: `${opp.probability}%`,               icon: Activity,   color: 'var(--blue-d)' },
          { label: 'Clôture prévue', value: fmtDate(opp.expectedCloseDate),   icon: Clock,      color: '#D97706' },
          { label: 'Activités',    value: activities.length.toString(),         icon: Activity,   color: 'var(--brand-amber)' },
        ].map(k => (
          <div key={k.label} className="bg-white border border-[var(--bd)] rounded-xl p-4">
            <p className="text-xs text-[var(--tx3)]">{k.label}</p>
            <p className="text-lg font-bold mt-1" style={{ color: k.color }}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* Infos supplémentaires */}
      <div className="bg-white border border-[var(--bd)] rounded-xl p-4 grid grid-cols-2 gap-4 text-sm">
        {opp.contact && <div><p className="text-xs text-[var(--tx3)]">Contact</p><p className="text-[var(--brand-dark)] font-medium">{opp.contact.firstName} {opp.contact.lastName}</p></div>}
        {opp.assignedTo && <div><p className="text-xs text-[var(--tx3)]">Assigné à</p><p className="text-[var(--brand-dark)] font-medium">{opp.assignedTo.user.firstName} {opp.assignedTo.user.lastName}</p></div>}
        {opp.source && <div><p className="text-xs text-[var(--tx3)]">Source</p><p className="text-[var(--brand-dark)]">{opp.source}</p></div>}
        {opp.wonLostReason && <div><p className="text-xs text-[var(--tx3)]">Raison</p><p className="text-[var(--brand-dark)]">{opp.wonLostReason}</p></div>}
        {opp.description && <div className="col-span-2"><p className="text-xs text-[var(--tx3)]">Description</p><p className="text-[var(--tx2)]">{opp.description}</p></div>}
      </div>

      {/* Séquence active banner */}
      {activeSeq && (
        <div className="flex items-center gap-3 p-3 bg-[#F0FDF4] border border-emerald-200 rounded-xl">
          <Zap className="w-4 h-4 text-emerald-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-emerald-800">Séquence active : {activeSeq.sequence?.name ?? '—'}</p>
            <p className="text-xs text-emerald-600">Étape {activeSeq.currentStep}/{activeSeq.sequence?.steps?.length ?? '?'}</p>
          </div>
          <button onClick={() => stopSeqMutation.mutate()} disabled={stopSeqMutation.isPending}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-emerald-200 text-emerald-700 rounded-lg text-xs font-medium">
            <Square className="w-3 h-3" /> Arrêter
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-[var(--bd)]">
        <div className="flex gap-1">
          {([
            { key: 'activites',   label: `Activités (${activities.length})` },
            { key: 'historique',  label: `Historique (${stageHistory.length})` },
            { key: 'commissions', label: `Commissions (${commissions.length})` },
            { key: 'sequences',   label: `Séquences (${seqList.length})` },
          ] as const).map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 ${tab === t.key ? 'border-[var(--blue)] text-[var(--brand-dark)]' : 'border-transparent text-[var(--tx3)] hover:text-[var(--brand-dark)]'}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {tab === 'activites' && (
        <div className="space-y-3">
          {activities.length === 0 ? (
            <div className="text-center py-8 text-[var(--tx3)] text-sm">Aucune activité enregistrée</div>
          ) : activities.map((a: any) => (
            <div key={a.id} className="flex gap-3 p-3 bg-white border border-[var(--bd)] rounded-xl">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-base shrink-0" style={{ background: '#F4F2FC' }}>
                {ACTIVITY_ICONS[a.type] ?? '📌'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-[var(--brand-dark)]">{a.subject}</p>
                  <ActivityCorrectedBadge activity={a} />
                </div>
                <p className="text-xs text-[var(--tx3)] mt-0.5">{a.description}</p>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-[var(--blue)]">→ {a.nextAction}</span>
                  <span className="text-xs text-[var(--tx3)]">{fmtDate(a.nextActionDate)}</span>
                </div>
                {(a.corrections?.length ?? 0) > 0 && (
                  <div className="mt-2.5">
                    <ActivityCorrectionList activity={a} />
                  </div>
                )}
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs text-[var(--tx3)]">{fmtDateTime(a.performedAt)}</p>
                {a.performedBy && <p className="text-xs text-[var(--tx3)]">{a.performedBy.firstName}</p>}
                <div className="flex justify-end -mr-2 mt-1">
                  <CorrectActivityButton activity={a} invalidateKeys={[['crm-activities'], ['crm-opp-detail'], ['crm-opp']]} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'historique' && (
        <div className="space-y-2">
          {stageHistory.length === 0 ? (
            <div className="text-center py-8 text-[var(--tx3)] text-sm">Aucun changement d'étape enregistré</div>
          ) : stageHistory.map((h: any) => (
            <div key={h.id} className="flex items-center gap-3 p-3 bg-white border border-[var(--bd)] rounded-xl">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-[var(--tx3)]">{h.fromStage?.name ?? 'Début'}</span>
                  <ChevronRight className="w-3 h-3 text-[var(--tx3)]" />
                  <span className="font-semibold text-[var(--brand-dark)]">{h.toStage?.name}</span>
                </div>
                {h.notes && <p className="text-xs text-[var(--tx3)] mt-0.5">{h.notes}</p>}
              </div>
              <p className="text-xs text-[var(--tx3)] shrink-0">{fmtDateTime(h.changedAt)}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'commissions' && (
        <div className="space-y-2">
          {commissions.length === 0 ? (
            <div className="text-center py-8 text-[var(--tx3)] text-sm">Aucune commission sur cette opportunité</div>
          ) : commissions.map((c: any) => {
            const cfg = STATUS_COLORS[c.status] ?? STATUS_COLORS.PENDING
            return (
              <div key={c.id} className="flex items-center gap-3 p-3 bg-white border border-[var(--bd)] rounded-xl">
                <span className="text-xs px-2 py-0.5 rounded-full font-medium shrink-0"
                  style={{ background: cfg.bg, color: cfg.color }}>
                  {c.status.replace(/_/g, ' ')}
                </span>
                <div className="flex-1">
                  <p className="text-sm font-bold text-[#059669]">{fmtMoney(c.amount, c.currency)}</p>
                  {(c.calculationDetail as any)?.planName && <p className="text-xs text-[var(--tx3)]">{(c.calculationDetail as any).planName}</p>}
                </div>
                <p className="text-xs text-[var(--tx3)]">{fmtDate(c.createdAt)}</p>
              </div>
            )
          })}
        </div>
      )}

      {tab === 'sequences' && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <button onClick={() => setShowSeqStart(true)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium"
              style={{ background: 'var(--brand-orange)', color: '#FFFFFF' }}>
              <Play className="w-3.5 h-3.5" /> Démarrer une séquence
            </button>
          </div>

          {seqList.length === 0 ? (
            <div className="text-center py-8 text-[var(--tx3)] text-sm">Aucune séquence sur cette opportunité</div>
          ) : seqList.map((s: any) => (
            <div key={s.id} className="p-3 bg-white border border-[var(--bd)] rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-[var(--brand-dark)]">{s.sequence?.name ?? s.sequenceId}</p>
                  <p className="text-xs text-[var(--tx3)]">Étape {s.currentStep}/{s.sequence?.steps?.length ?? '?'} · Démarré {fmtDate(s.startedAt)}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' : s.status === 'STOPPED' ? 'bg-red-50 text-red-700' : 'bg-[var(--surface-2)] text-[var(--tx3)]'}`}>
                  {s.status}
                </span>
              </div>

              {/* Approve step si en attente */}
              {s.status === 'ACTIVE' && s.pendingStepId && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
                  <p className="text-xs text-amber-700">⏳ Étape {s.currentStep + 1} en attente d'approbation</p>
                  <button onClick={() => approveStepMutation.mutate({ oppSeqId: s.id, stepId: s.pendingStepId })}
                    disabled={approveStepMutation.isPending}
                    className="flex items-center gap-1.5 px-3 py-1 bg-amber-500 text-white rounded-lg text-xs font-medium">
                    {approveStepMutation.isPending ? <Spinner /> : <Check className="w-3 h-3" />} Approuver l'envoi
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showJournal && (
        <JournalQuickModal opportunityId={id!}
          onClose={() => setShowJournal(false)}
          onSaved={() => { queryClient.invalidateQueries({ queryKey: ['crm-opp-detail', id] }); setShowJournal(false) }}
        />
      )}
      {showSeqStart && (
        <StartSeqModal opportunityId={id!}
          onClose={() => setShowSeqStart(false)}
          onStarted={() => { queryClient.invalidateQueries({ queryKey: ['crm-opp-detail', id] }); setShowSeqStart(false) }}
        />
      )}
    </div>
  )
}

// ── Mini modals ───────────────────────────────────────────────────────────────

const inputCls = 'w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--brand-orange)]'

import { X } from 'lucide-react'
import React from 'react'

function ModalWrap({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
      <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-md shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-[var(--bd)] shrink-0">
          <h2 className="text-base font-semibold text-[var(--brand-dark)]">{title}</h2>
          <button onClick={onClose}><X className="w-4 h-4 text-[var(--tx3)]" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">{children}</div>
      </div>
    </div>
  )
}

function JournalQuickModal({ opportunityId, onClose, onSaved }: { opportunityId: string; onClose: () => void; onSaved: () => void }) {
  const addNotif = useNotificationStore(s => s.addNotification)
  const [form, setForm] = useState({ type: 'CALL', subject: '', description: '', nextAction: '', nextActionDate: '' })
  const f = (k: string) => (e: any) => setForm(p => ({ ...p, [k]: e.target.value }))
  const { mutate, isPending } = useMutation({
    mutationFn: () => crmApi.createActivity({ ...form, opportunityId }),
    onSuccess:  () => { addNotif({ id: Date.now().toString(), type: 'success', title: 'Activité enregistrée' }); onSaved() },
  })
  return (
    <ModalWrap title="Journal d'activité" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="block text-xs text-[var(--tx3)] mb-1">Type</label><select className={inputCls} value={form.type} onChange={f('type')}>{['CALL','EMAIL','MEETING','VIDEO_CALL','WHATSAPP','DEMO','NOTE','OTHER'].map(t => <option key={t} value={t}>{t}</option>)}</select></div>
        <div><label className="block text-xs text-[var(--tx3)] mb-1">Date action suiv.</label><input className={inputCls} type="date" value={form.nextActionDate} onChange={f('nextActionDate')} /></div>
      </div>
      <div><label className="block text-xs text-[var(--tx3)] mb-1">Sujet *</label><input className={inputCls} value={form.subject} onChange={f('subject')} autoFocus /></div>
      <div><label className="block text-xs text-[var(--tx3)] mb-1">Compte-rendu *</label><textarea className={inputCls + ' resize-none'} rows={3} value={form.description} onChange={f('description')} /></div>
      <div><label className="block text-xs text-[var(--tx3)] mb-1">Prochaine action *</label><input className={inputCls} value={form.nextAction} onChange={f('nextAction')} /></div>
      <div className="flex justify-end gap-3"><button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)]">Annuler</button><button onClick={() => mutate()} disabled={isPending || !form.subject || !form.description || !form.nextAction || !form.nextActionDate} className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50" style={{ background: 'var(--brand-orange)', color: '#FFFFFF' }}>{isPending ? <Spinner /> : <Check className="w-4 h-4" />} Enregistrer</button></div>
    </ModalWrap>
  )
}

function StartSeqModal({ opportunityId, onClose, onStarted }: { opportunityId: string; onClose: () => void; onStarted: () => void }) {
  const addNotif = useNotificationStore(s => s.addNotification)
  const [sequenceId, setSequenceId] = useState('')
  const [requiresApproval, setRequiresApproval] = useState(false)
  const { data: seqData } = useQuery({ queryKey: ['crm-sequences-active'], queryFn: () => crmApi.listSequences({ isActive: true }) })
  const sequences = seqData?.items ?? []
  const { mutate, isPending } = useMutation({
    mutationFn: () => crmApi.startSequenceOnOpp(opportunityId, { sequenceId, requiresApproval }),
    onSuccess:  () => { addNotif({ id: Date.now().toString(), type: 'success', title: 'Séquence démarrée' }); onStarted() },
    onError:    (err: any) => addNotif({ id: Date.now().toString(), type: 'error', title: err?.response?.data?.message ?? 'Erreur' }),
  })
  return (
    <ModalWrap title="Démarrer une séquence" onClose={onClose}>
      <div><label className="block text-xs text-[var(--tx3)] mb-1">Séquence *</label><select className={inputCls} value={sequenceId} onChange={e => setSequenceId(e.target.value)}><option value="">Sélectionner…</option>{(sequences as any[]).map((s: any) => <option key={s.id} value={s.id}>{s.name} ({s.steps?.length ?? 0} étapes)</option>)}</select></div>
      <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={requiresApproval} onChange={e => setRequiresApproval(e.target.checked)} className="rounded" /><span className="text-xs text-[var(--tx2)]">Mode approbation — chaque email attend votre validation avant envoi</span></label>
      <div className="flex justify-end gap-3"><button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)]">Annuler</button><button onClick={() => mutate()} disabled={isPending || !sequenceId} className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50" style={{ background: 'var(--brand-orange)', color: '#FFFFFF' }}>{isPending ? <Spinner /> : <Play className="w-4 h-4" />} Démarrer</button></div>
    </ModalWrap>
  )
}

export default OpportunityDetailPage
